import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Phone, Send, Loader2 } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

export default function Contact() {
  const { t, locale } = useI18n();
  const [form, setForm] = useState({
    name: "",
    email: "",
    instagram: "",
    fitness_level: "",
    goals: "",
    budget: "",
  });
  const [status, setStatus] = useState({ loading: false, ok: null, error: null });

  const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.fitness_level || !form.goals || !form.budget) {
      setStatus({ loading: false, ok: false, error: t.contact.error });
      return;
    }
    setStatus({ loading: true, ok: null, error: null });
    try {
      const res = await fetch("https://formsubmit.co/ajax/jorge.aguilar.valenzuela@gmail.com", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          instagram: form.instagram || "N/A",
          fitness_level: form.fitness_level,
          goals: form.goals,
          budget: form.budget,
          locale,
          _subject: "Nueva solicitud de coaching - Masterfit",
          _captcha: "false",
        }),
      });
      if (!res.ok) throw new Error("failed");
      setStatus({ loading: false, ok: true, error: null });
      setForm({ name: "", email: "", instagram: "", fitness_level: "", goals: "", budget: "" });
    } catch (err) {
      setStatus({ loading: false, ok: false, error: t.contact.error });
    }
  };

  return (
    <section id="contact" className="relative py-24 sm:py-32 border-t border-white/5" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <div className="text-xs tracking-[0.4em] text-[#FF6B00] uppercase">{t.contact.eyebrow}</div>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-4 uppercase leading-[0.95]">
            {t.contact.title}
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">{t.contact.subtitle}</p>

          <div className="mt-12 space-y-6">
            <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500">{t.contact.info}</div>
            <a
              href="https://wa.me/524423225843"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-4 group"
              data-testid="contact-whatsapp"
            >
              <Phone className="text-[#FF6B00] mt-1" size={20} />
              <div>
                <div className="text-sm text-zinc-400">WhatsApp</div>
                <div className="font-display text-2xl tracking-wide group-hover:text-[#FF6B00] transition">
                  +52 442 322 5843
                </div>
              </div>
            </a>
            <a
              href="mailto:jorge.aguilar.valenzuela@gmail.com"
              className="flex items-start gap-4 group"
              data-testid="contact-email"
            >
              <Mail className="text-[#FF6B00] mt-1" size={20} />
              <div>
                <div className="text-sm text-zinc-400">Email</div>
                <div className="text-base sm:text-lg group-hover:text-[#FF6B00] transition break-all">
                  jorge.aguilar.valenzuela@gmail.com
                </div>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <MapPin className="text-[#FF6B00] mt-1" size={20} />
              <div>
                <div className="text-sm text-zinc-400">{locale === "es" ? "Ubicación" : "Location"}</div>
                <div className="text-base sm:text-lg">{t.contact.location}</div>
              </div>
            </div>
          </div>

          <a
            href="https://wa.me/524423225843?text=Hola%20Jorge%2C%20quiero%20agendar%20una%20llamada%20de%20coaching."
            target="_blank"
            rel="noopener noreferrer"
            className="mt-10 inline-flex items-center gap-2 bg-[#25D366] text-black font-bold text-xs tracking-[0.25em] py-4 px-6 uppercase btn-glow-green hover:scale-[1.02] transition"
            data-testid="contact-schedule"
          >
            {t.contact.schedule}
          </a>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          onSubmit={handleSubmit}
          className="lg:col-span-7 glass-card p-8 sm:p-10"
          data-testid="application-form"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <input
              required
              className="mf-input"
              placeholder={t.contact.name}
              value={form.name}
              onChange={handleChange("name")}
              data-testid="form-name"
            />
            <input
              required
              type="email"
              className="mf-input"
              placeholder={t.contact.email}
              value={form.email}
              onChange={handleChange("email")}
              data-testid="form-email"
            />
            <input
              className="mf-input"
              placeholder={t.contact.instagram}
              value={form.instagram}
              onChange={handleChange("instagram")}
              data-testid="form-instagram"
            />
            <select
              required
              className="mf-input"
              value={form.fitness_level}
              onChange={handleChange("fitness_level")}
              data-testid="form-level"
            >
              <option value="" disabled>
                {t.contact.level}
              </option>
              {t.contact.levels.map((lv) => (
                <option key={lv} value={lv}>
                  {lv}
                </option>
              ))}
            </select>
            <select
              required
              className="mf-input sm:col-span-2"
              value={form.budget}
              onChange={handleChange("budget")}
              data-testid="form-budget"
            >
              <option value="" disabled>
                {t.contact.budget}
              </option>
              {t.contact.budgets.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
            <textarea
              required
              rows={4}
              className="mf-input sm:col-span-2 resize-none"
              placeholder={t.contact.goals}
              value={form.goals}
              onChange={handleChange("goals")}
              data-testid="form-goals"
            />
          </div>

          <button
            type="submit"
            disabled={status.loading}
            className="mt-10 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#FF6B00] text-black font-bold text-xs tracking-[0.3em] py-4 px-10 uppercase btn-glow disabled:opacity-50"
            data-testid="form-submit"
          >
            {status.loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {status.loading ? t.contact.submitting : t.contact.submit}
          </button>

          {status.ok === true && (
            <div className="mt-6 text-sm text-green-400" data-testid="form-success">
              ✓ {t.contact.success}
            </div>
          )}
          {status.ok === false && (
            <div className="mt-6 text-sm text-red-400" data-testid="form-error">
              {status.error}
            </div>
          )}
        </motion.form>
      </div>
    </section>
  );
}
